import React, { useState } from 'react';
import { VolumeCard } from '../components/VolumeCard';
import { VolumeChart } from '../components/VolumeChart';
import { useVolumeData, useTimeSeriesData } from '../hooks/useVolumeData';
import { TimePeriod } from '../types';

export const Dashboard: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('daily');
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');
  const [showMintBurn, setShowMintBurn] = useState(true);

  const { summary, stats, health, loading, error, refreshData } = useVolumeData();
  const { data: timeSeriesData, loading: chartLoading } = useTimeSeriesData(selectedPeriod, 30);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-xl mb-4">⚠️ Error Loading Dashboard</div>
          <p className="text-gray-400 mb-4">{error}</p>
          <button 
            onClick={refreshData}
            className="btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Background Effects */}
      <div className="absolute -top-20 -left-20 w-64 h-64 bg-blue-500/20 rounded-full filter blur-3xl opacity-50 animate-pulse"></div>
      <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-purple-500/20 rounded-full filter blur-3xl opacity-50 animate-pulse" style={{ animationDelay: '2s' }}></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-green-800/10 rounded-full filter blur-3xl opacity-30 animate-pulse" style={{ animationDelay: '1s' }}></div>

      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500 mb-2 filter drop-shadow-sm">
            Shido Gateway Volume Dashboard
          </h1>
          <p className="text-gray-300 mb-4 backdrop-blur-sm bg-gray-800/10 inline-block px-3 py-1 rounded-lg">
            Real-time volume tracking for USDC and USDT gateway transactions
          </p>
          
          {/* Status Indicator */}
          {health && (
            <div className="flex items-center justify-center space-x-4 text-sm">
              <div className={`flex items-center space-x-2 ${health.monitor.isRunning ? 'text-green-400' : 'text-red-400'}`}>
                <div className={`w-2 h-2 rounded-full ${health.monitor.isRunning ? 'bg-green-400' : 'bg-red-400'} animate-pulse`}></div>
                <span>Monitor: {health.monitor.isRunning ? 'Online' : 'Offline'}</span>
              </div>
              <div className="text-gray-400">
                Last Update: {new Date(health.timestamp).toLocaleTimeString()}
              </div>
            </div>
          )}
        </div>

        {/* Volume Cards */}
        {summary && (
          <div className="dashboard-grid mb-8">
            <VolumeCard 
              data={summary.USDC} 
              icon="/images/usdc.png"
              color="blue"
            />
            <VolumeCard 
              data={summary.USDT} 
              icon="/images/usdt.png"
              color="purple"
            />
            <VolumeCard 
              data={summary.TOTAL} 
              color="green"
            />
          </div>
        )}

        {/* Chart Controls */}
        <div className="glass p-4 rounded-xl border border-gray-700/50 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <label className="text-sm text-gray-300">Time Period:</label>
              <select 
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value as TimePeriod)}
                className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1 text-white text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <div className="flex items-center space-x-4">
              <label className="text-sm text-gray-300">Chart Type:</label>
              <select 
                value={chartType}
                onChange={(e) => setChartType(e.target.value as 'line' | 'bar')}
                className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1 text-white text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="line">Line Chart</option>
                <option value="bar">Bar Chart</option>
              </select>
            </div>

            <div className="flex items-center space-x-4">
              <label className="text-sm text-gray-300">View:</label>
              <select 
                value={showMintBurn ? 'mintburn' : 'tokens'}
                onChange={(e) => setShowMintBurn(e.target.value === 'mintburn')}
                className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1 text-white text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="mintburn">Mint/Burn</option>
                <option value="tokens">By Token</option>
              </select>
            </div>

            <button 
              onClick={refreshData}
              className="btn-secondary text-sm"
              disabled={loading}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
          <VolumeChart 
            data={timeSeriesData}
            type={chartType}
            showMintBurn={showMintBurn}
            height={400}
          />
          <VolumeChart 
            data={timeSeriesData}
            type={chartType === 'line' ? 'bar' : 'line'}
            showMintBurn={!showMintBurn}
            height={400}
          />
        </div>

        {/* Statistics */}
        {stats && (
          <div className="glass p-6 rounded-xl border border-gray-700/50">
            <h3 className="text-xl font-semibold text-white mb-4">Gateway Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="metric-value">{stats.totalTransactions.toLocaleString()}</div>
                <div className="metric-label">Total Transactions</div>
              </div>
              <div className="text-center">
                <div className="metric-value">{parseFloat(stats.totalVolume).toFixed(2)}</div>
                <div className="metric-label">Total Volume</div>
              </div>
              <div className="text-center">
                <div className="metric-value">{parseFloat(stats.averageTransactionSize).toFixed(2)}</div>
                <div className="metric-label">Avg Transaction Size</div>
              </div>
              <div className="text-center">
                <div className={`metric-value ${parseFloat(stats.netFlow) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {parseFloat(stats.netFlow) >= 0 ? '+' : ''}{parseFloat(stats.netFlow).toFixed(2)}
                </div>
                <div className="metric-label">Net Flow</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-gray-700/50">
              <div>
                <h4 className="text-lg font-medium text-white mb-3">USDC Statistics</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Transactions:</span>
                    <span className="text-white">{stats.tokens.USDC.transactions.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Volume:</span>
                    <span className="text-white">{parseFloat(stats.tokens.USDC.volume).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Avg Size:</span>
                    <span className="text-white">{parseFloat(stats.tokens.USDC.averageSize).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-medium text-white mb-3">USDT Statistics</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Transactions:</span>
                    <span className="text-white">{stats.tokens.USDT.transactions.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Volume:</span>
                    <span className="text-white">{parseFloat(stats.tokens.USDT.volume).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Avg Size:</span>
                    <span className="text-white">{parseFloat(stats.tokens.USDT.averageSize).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm flex flex-col gap-1">
          <p>Shido Gateway Volume Dashboard - Real-time volume tracking</p>
          <p>Data updates every 60 seconds</p>
          <p className="text-xs text-gray-500 font-medium mt-1 opacity-80">Powered by MavNode Labs 🧪</p>
        </div>
      </div>
    </div>
  );
};
